import { Box, Grid, Typography } from "@mui/material";
import { useAtom } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAlert } from "../../../../../lib/alert";
import { useProcessAnimationContext } from "../../../../process/components/AnimationContext";
import ConfirmationModal from "../../../../process/components/confirmation-modal/ConfirmationModal";
import { useProcessExecutionContext } from "../../../../process/hooks";
import { taskEditingAtom } from "../../../hooks/useTaskExecutionEditingState";
import {
  ButtonGroupFormElement,
  FormElementType,
  TaskExecutionState,
  TaskForm,
  TaskFormExecution,
  TaskViews,
  TypographyFormElement,
} from "../../../types";
import { DEFAULT_RENDER } from "../TaskBody";
import { executeWizardFormTask } from "./executeFormTask";
import getCommonFormExecutionComponent from "./FormCommonExecutionComponents";
import { FormExecutionComponentProps } from "./FormExecutionComponent";
import {
  ButtonGroupFunction,
  ButtonGroupSettings,
  TaskButtonGroupFormComponent,
} from "./wizard-form-elements/TaskButtonGroupFormComponent";
import { TaskSummaryFormComponent } from "./wizard-form-elements/TaskSummaryFormComponent";

const WizardFormExecutionComponent = ({
  children = DEFAULT_RENDER,
  onExecute,
  ...props
}: FormExecutionComponentProps) => {
  const { error } = useAlert();
  const anim = useProcessAnimationContext();
  const { executeTask } = useProcessExecutionContext();
  const [editing, setEditing] = useAtom(taskEditingAtom);

  const [openConfirmationModal, setOpenConfirmationModal] =
    useState<boolean>(false);

  // cast input data so TS compiler doesn't bark
  const propsTask = props.task as TaskForm;
  const propsExecution = props.taskExecution as TaskFormExecution | undefined;

  useEffect(() => {
    if (editing === undefined) {
      setEditing(propsExecution?.state !== TaskExecutionState.Completed);
    }
  }, [editing, propsExecution?.state, setEditing]);

  const trueElements = useMemo(() => {
    return props.elements ?? propsTask.data?.form?.elements ?? [];
  }, [props.elements, propsTask.data?.form?.elements]);

  const nested = props.elements && props.elements.length > 0;
  const padDescription =
    props.view === TaskViews.CONDENSED &&
    !!props.task.description &&
    props.task.description.length > 0;

  const stepIndexes = useMemo(() => {
    const steps: number[][] = [];
    let currentStepIndexes: number[] = [];

    trueElements.forEach((element, index) => {
      if (
        element.type === FormElementType.Heading &&
        currentStepIndexes.length > 0
      ) {
        steps.push(currentStepIndexes);
        currentStepIndexes = [];
      }
      currentStepIndexes.push(index);
    });

    if (currentStepIndexes.length > 0) {
      steps.push(currentStepIndexes);
    }

    return steps;
  }, [trueElements]);

  const [disabled, setDisabled] = useState<boolean>(
    !(editing ?? !props.isDisabled),
  );

  const [validationIndexes, setValidationIndexes] = useState<Array<number>>(
    propsExecution?.data?.wizardData?.validationIndexes || [...stepIndexes[0]],
  );

  useEffect(() => {
    setDisabled(!(editing ?? !props.isDisabled));
  }, [editing, props.isDisabled, props.taskExecution]);

  const [currentStep, setCurrentStep] = useState(
    propsExecution?.data?.wizardData?.currentStep || 0,
  );

  const saveExecutionData = useCallback(
    async (
      step?: number,
      saveOnly?: boolean,
      updatedValidationIndexes?: Array<number>,
    ) => {
      if (!saveOnly) saveOnly = false;
      try {
        const execution = {
          ...propsExecution,
          task_id: propsTask.id,
          state: TaskExecutionState.Completed,
          data: {
            ...executeWizardFormTask(
              propsTask,
              updatedValidationIndexes || [],
              saveOnly,
            ).data,
            wizardData: {
              ...propsExecution?.data?.wizardData,
              ...(step !== undefined && { currentStep: step }),
              validationIndexes: updatedValidationIndexes ?? validationIndexes,
            },
          },
        } as TaskFormExecution;

        if (onExecute) {
          await onExecute(execution, !saveOnly).then(() => {
            anim?.attemptToExecuteAnimation();
          });
        } else {
          await executeTask(execution, !saveOnly).then(() => {
            anim?.attemptToExecuteAnimation();
          });
        }

        if (!saveOnly) {
          setEditing(false);
        }
      } catch (err: unknown) {
        error(String(err));
      }
    },
    [
      anim,
      error,
      executeTask,
      onExecute,
      propsExecution,
      propsTask,
      setEditing,
      validationIndexes,
    ],
  );

  const gotoStep = useCallback(
    (newStep: number) => {
      if (newStep >= 0 && newStep < stepIndexes.length) {
        setCurrentStep(newStep);

        if (newStep > currentStep) {
          const nextStepIndexes = stepIndexes[newStep] || [];
          setValidationIndexes((prev) => {
            const updatedIndexes = [
              ...prev,
              ...nextStepIndexes.filter((idx) => !prev.includes(idx)),
            ];
            saveExecutionData(newStep, true, updatedIndexes);
            return updatedIndexes;
          });
        } else if (newStep < currentStep) {
          const allowedIndexes = stepIndexes.slice(0, newStep + 1).flat();
          setValidationIndexes((prev) => {
            const updatedIndexes = prev.filter((idx) =>
              allowedIndexes.includes(idx),
            );
            saveExecutionData(newStep, true, updatedIndexes);
            return updatedIndexes;
          });
        }
      }
    },
    [currentStep, saveExecutionData, stepIndexes],
  );

  const validateElements = useCallback(() => {
    const currentStepIndexes = stepIndexes[currentStep] || [];
    for (const index of currentStepIndexes) {
      const element = trueElements[index];
      if (!element) continue;

      if (!element.required) {
        continue;
      }

      switch (element.type) {
        case FormElementType.Text:
        case FormElementType.Address:
        case FormElementType.Date:
        case FormElementType.Upload:
          if (!element.value || element.value === "") {
            return false;
          }
          break;
        case FormElementType.Radio:
        case FormElementType.Selection:
        case FormElementType.MultiSelection:
          if (
            !element.selection ||
            (Array.isArray(element.selection) && element.selection.length === 0)
          ) {
            return false;
          }
          break;
        case FormElementType.Checkbox:
          if (element.value === undefined) {
            return false;
          }
          break;
        case FormElementType.FieldDefinition:
          if (!element.value || element.value.length === 0) {
            return false;
          }
          break;
        default:
          break;
      }
    }
    return true;
  }, [currentStep, stepIndexes, trueElements]);

  const gotoPreviousStep = useCallback(() => {
    // This calculates the previous step based on the
    // indexes that were previously accessed
    const indexesToRemove = stepIndexes[currentStep];
    const newValidationIndexes = validationIndexes.filter(
      (index) => !indexesToRemove.includes(index),
    );
    const newStep = stepIndexes.findIndex((step) =>
      step.includes(newValidationIndexes[newValidationIndexes.length - 1]),
    );
    setValidationIndexes(newValidationIndexes);
    gotoStep(newStep);
  }, [currentStep, gotoStep, stepIndexes, validationIndexes]);

  const handleButtonGroupAction = useCallback(
    (settings: ButtonGroupSettings) => {
      switch (settings.assignment) {
        case ButtonGroupFunction.NEXT_STEP:
          if (validateElements()) {
            gotoStep(currentStep + 1);
          } else {
            error("Please complete all required fields before proceeding.");
          }
          break;

        case ButtonGroupFunction.PREV_STEP:
          gotoPreviousStep();
          break;

        case ButtonGroupFunction.SUBMIT_TASK:
          props.task?.data?.confirmation
            ? setOpenConfirmationModal(true)
            : saveExecutionData(0, false, validationIndexes);
          break;

        case ButtonGroupFunction.GOTO_STEP:
          if (validateElements()) {
            if (typeof settings.data === "number" && !isNaN(settings.data)) {
              gotoStep(settings.data);
            } else {
              error("Invalid step index.");
            }
          } else {
            error("Please complete all required fields before proceeding.");
          }
          break;
      }
    },
    [
      validateElements,
      gotoPreviousStep,
      props.task?.data?.confirmation,
      saveExecutionData,
      validationIndexes,
      gotoStep,
      currentStep,
      error,
    ],
  );

  const contents = useMemo(
    () =>
      trueElements.map((element, index) => {
        const formElementExecution = (
          props.executionElements ??
          propsExecution?.data?.form?.elements ??
          []
        ).find(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (e: any) => e.id == element.id,
        );

        let elem;
        elem = getCommonFormExecutionComponent({
          element: element,
          trueElements: trueElements,
          index: index,
          state: propsExecution?.state,
          formElementExecution: formElementExecution,
          disabled: disabled,
          nested: nested,
          task: props.task,
          validElements: validationIndexes,
        });

        if (!elem)
          switch (element.type) {
            // Wizard Specific Elements:
            case FormElementType.ButtonGroup:
              elem = (
                <TaskButtonGroupFormComponent
                  {...(element as ButtonGroupFormElement)}
                  execution={formElementExecution}
                  disabled={disabled}
                  key={element.id}
                  onClick={handleButtonGroupAction}
                />
              );
              break;
            case FormElementType.Typography:
              elem = (
                <Typography
                  sx={{
                    py: 1,
                  }}
                  variant={
                    (element as TypographyFormElement).variant || "body1"
                  }
                  textAlign={
                    (element as TypographyFormElement).textAlign || "left"
                  }
                >
                  {formElementExecution?.label}
                </Typography>
              );
              break;
            case FormElementType.Summary:
              if (propsExecution)
                elem = (
                  <TaskSummaryFormComponent
                    disabled={disabled}
                    execution={propsExecution}
                    onGoToStep={(num) => {
                      gotoStep(num);
                    }}
                  />
                );
              else return;
              break;
            default:
              return;
          }

        if (!elem)
          console.warn(
            `Unable to map element of type ${element.type} to a corresponding JSX element in the Wizard renderer.`,
          );

        return elem;
      }),
    [
      trueElements,
      props.executionElements,
      props.task,
      propsExecution,
      disabled,
      nested,
      validationIndexes,
      handleButtonGroupAction,
      gotoStep,
    ],
  );

  const content = (
    <Grid
      container
      rowSpacing={props.isDisabled || !editing ? 1 : 2}
      direction="column"
      alignContent="stretch"
      justifyContent="center"
      pt={padDescription ? 2 : 0}
      pl={nested ? 3 : 0}
      width={"100%"}
      minWidth={"35vw"}
      sx={{
        pt: 2,
        "@media print": {
          paddingBottom: "64px", // Make all content visible for printing
        },
      }}
    >
      {trueElements.map((_, index) => {
        const isVisible = stepIndexes[currentStep]?.includes(index);
        return (
          <Box
            key={index}
            sx={{
              visibility: isVisible ? "visible" : "hidden",
              opacity: isVisible ? 1 : 0,
              transition:
                "opacity 0.3s ease-in-out, visibility 0.3s ease-in-out",
              maxHeight: isVisible ? "500px" : "0px",
              overflow: "hidden",
            }}
          >
            {contents[index]}
          </Box>
        );
      })}
      <ConfirmationModal
        executeModal={() => {
          saveExecutionData(0, false);
        }}
        openModal={openConfirmationModal}
        confirmationData={props.task?.data?.confirmation}
        handleCloseModal={() => {
          setOpenConfirmationModal(false);
        }}
      />
    </Grid>
  );

  return children({ content });
};

export default WizardFormExecutionComponent;
